/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */
import { LogLevel } from "@azure/msal-browser";
import { PublicClientApplication } from '@azure/msal-browser';

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 *
 * All client-specific values now come from environment variables.
 * Set these via .env.<client> files - see .env.atlas / .env.pascal / .env.unic / .env.localtest
 *
 * NOTE: dotenv-webpack does a static text replacement at build time — it can only
 * inject values for literal `process.env.SOME_NAME` references in the source.
 * A dynamic lookup like `process.env[someVariable]` will NOT be replaced and will
 * always resolve to undefined in the browser. So every var is read as its own
 * literal `process.env.X` const below, never via a loop or computed key.
 */
const REACT_APP_CLIENT = process.env.REACT_APP_CLIENT;
const REACT_APP_AAD_CLIENT_ID = process.env.REACT_APP_AAD_CLIENT_ID;
const REACT_APP_AAD_TENANT_ID = process.env.REACT_APP_AAD_TENANT_ID;
const REACT_APP_REDIRECT_URI = process.env.REACT_APP_REDIRECT_URI;
const REACT_APP_AUTH_SCOPE = process.env.REACT_APP_AUTH_SCOPE;

// Fail loudly at build/start time if a client env file wasn't loaded correctly,
// instead of silently shipping with the wrong tenant.
const missing = [];
if (!REACT_APP_CLIENT) missing.push("REACT_APP_CLIENT");
if (!REACT_APP_AAD_CLIENT_ID) missing.push("REACT_APP_AAD_CLIENT_ID");
if (!REACT_APP_AAD_TENANT_ID) missing.push("REACT_APP_AAD_TENANT_ID");
if (!REACT_APP_REDIRECT_URI) missing.push("REACT_APP_REDIRECT_URI");
if (!REACT_APP_AUTH_SCOPE) missing.push("REACT_APP_AUTH_SCOPE");

if (missing.length > 0) {
  throw new Error(
    `Missing required MSAL env vars: ${missing.join(", ")}. ` +
    `Did you forget to run with the correct client env file (e.g. -f .env.atlas)?`
  );
}

const msalConfig = {
  auth: {
    clientId: REACT_APP_AAD_CLIENT_ID,
    authority: `https://login.microsoftonline.com/${REACT_APP_AAD_TENANT_ID}/v2.0`,
    redirectUri: REACT_APP_REDIRECT_URI,
    postLogoutRedirectUri: REACT_APP_REDIRECT_URI,
    navigateToLoginRequestUrl: false,
  },
  cache: {
    cacheLocation: "localStorage",
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, piiLoggingEnabled) => {
        if (level === LogLevel.Error) {
          console.error(message);
        } else if (level === LogLevel.Warning) {
          // console.log('Warning', message);
        } else {
          // console.log(message);
        }
      },
      logLevel: LogLevel.Verbose,
      piiLoggingEnabled: false,
    },
  },
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
  scopes: [REACT_APP_AUTH_SCOPE],
};

export const msalInstance = new PublicClientApplication(msalConfig);
// export const msalInstance = await PublicClientApplication.createPublicClientApplication(msalConfig);